// =============================================================================
// OS SIMPLIFICADA - permissão de empresa "CRIAÇÃO DE OS SIMPLIFICADA".
//
// Fluxo pedido pelo cliente (analise_do_cliente.md):
//   Nova O.S. → Cliente → Serviços → Técnico responsável → Revisão → Imprimir
//
// É um caminho CURTO ao lado do formulário completo (OSNova, 8 passos), não um
// substituto: o link "usar o formulário completo" continua levando ao de sempre,
// onde ficam localização, equipamento, prioridade, orçamento e evidências.
//
// AUTOSSUFICIENTE: esta tela só depende da permissão "CRIAÇÃO DE OS SIMPLIFICADA".
// O serviço é lançado com descrição e valor digitados na hora, e o campo sugere o
// que a empresa já lançou antes (com o último valor usado). Se o tenant TAMBÉM
// tiver tabela de preços cadastrada, ela aparece como atalho - mas é bônus, nunca
// requisito.
//
// Os serviços são gravados na MESMA transação da OS (os/os_criar.php), com
// quantidade, valor unitário e subtotal. Serviço sem valor informado aparece como
// "sem preço" e não entra no total - somar zero mentiria sobre o combinado.
// =============================================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, getToken } from '../lib/api';
import { API_BASE } from '../config';
import { useAuth } from '../lib/auth';
import { mascaraMoedaBR, moedaParaNumero, numeroParaMascaraMoeda } from '../lib/moeda';
import Passos, { Passo } from '../components/Passos';

interface Opcao { id: number; nome: string; }
interface Tecnico extends Opcao { unidades?: number[]; todas_unidades?: boolean; }
interface Cliente extends Opcao { whatsapp?: string | null; }
interface MaterialCatalogo {
  id: number;
  nome: string;
  codigo?: string | null;
  unidade_medida?: string | null;
  quantidade_atual: string | number;
  valor_unitario: string | number;
  status?: string | null;
}
interface Ativo {
  id: number;
  nome: string;
  patrimonio?: string | null;
  status?: string | null;
  cliente_ids?: number[];
}

// Cadastro unificado do cliente (mesma permissão): o que já está cadastrado para
// o cliente escolhido, vindo de os/os_cliente_contexto.php numa chamada.
interface AmbienteCliente {
  id: number;
  nome: string;
  area_m2: number | null;
  ocupantes_max: number | null;
  atividade_ambiente: string | null;
  observacao: string | null;
  equipamentos: number;
  status?: string | null;
}
interface EquipamentoCliente {
  id: number;
  nome: string;
  patrimonio: string | null;
  marca: string | null;
  modelo: string | null;
  numero_serie: string | null;
  capacidade_btu: number | null;
  cliente_ambiente_id: number | null;
  ambiente_nome: string | null;
  categoria_nome: string | null;
  status?: string | null;
}
interface DocumentoCliente {
  id: number;
  tipo: string;
  titulo: string;
  data_emissao: string | null;
  data_validade: string | null;
}
interface ContextoCliente {
  cliente: {
    id: number;
    nome: string;
    documento?: string | null;
    telefone?: string | null;
    whatsapp?: string | null;
    email?: string | null;
    endereco_linha?: string | null;
  };
  ambientes: AmbienteCliente[];
  equipamentos: EquipamentoCliente[];
  documentos: DocumentoCliente[];
}

interface ServicoOpcao {
  id: number;
  rotulo: string;
  valor_unitario: string | number | null;
  sem_preco: boolean;
}

interface Sugestao {
  descricao: string;
  valor_unitario: string | number | null;
}

interface ServicosResposta {
  opcoes: ServicoOpcao[];
  sugestoes: Sugestao[];
  filtro: { tipo_os: string | null; tipo_os_original: string | null; total: number; tem_tabela_precos: boolean };
  pode_ver_valores: boolean;
}

interface ItemServico {
  // Chave local da linha. Não uso servico_id como key porque a lista aceita o
  // mesmo serviço mais de uma vez (duas visitas, valores diferentes), e serviço
  // digitado nem tem id.
  linha: number;
  servico_id: number | null;
  descricao: string;
  valor_unitario: number | null;
  quantidade: number;
}
interface ItemMaterial {
  linha: number;
  material_id: number;
  nome: string;
  valor_unitario: number;
  quantidade: number;
  unidade_medida?: string | null;
}

const moeda = (v: number): string => 'R$ ' + v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const numeroOuNulo = (v: string | number | null): number | null => {
  if (v === null || v === '') return null;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};
const valorMoedaOuNulo = (v: string | number | null): number | null => {
  if (v === null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const n = moedaParaNumero(v);
  return Number.isFinite(n) ? n : null;
};
const btuTexto = (v: number | null): string => (v ? v.toLocaleString('pt-BR', { maximumFractionDigits: 0 }) + ' BTU' : '');
const areaTexto = (v: number | null): string => (v ? v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' m²' : '');
const dataTexto = (v: string | null): string => (v ? String(v).slice(0, 10).split('-').reverse().join('/') : '');
// Rótulo do equipamento no select: nome + onde fica + o que é. Sem isso, um cliente
// com cinco splits iguais no nome viraria cinco opções indistinguíveis.
const rotuloEquipamentoCliente = (e: EquipamentoCliente): string => [
  e.nome,
  e.ambiente_nome ? 'Ambiente: ' + e.ambiente_nome : '',
  [e.marca, e.modelo].filter(Boolean).join(' '),
  btuTexto(e.capacidade_btu),
  e.patrimonio ? 'Patrim.: ' + e.patrimonio : '',
].filter(Boolean).join(' · ');

const qtdTexto = (q: number): string => q.toLocaleString('pt-BR', { maximumFractionDigits: 3, useGrouping: false });

const NOVO_VAZIO = { servico_id: '', material_id: '', descricao: '', valor_unitario: '', quantidade: '1' };
const MATERIAL_VAZIO = { material_id: '', valor_unitario: '', quantidade: '1' };

export default function OSNovaSimplificada() {
  const nav = useNavigate();
  const { user } = useAuth();
  const podeVerValoresMonetarios = !user?.somente_gestor_supervisor_pode_ver_valores_habilitado
    || ['admin_geral', 'admin_empresa', 'supervisor'].includes(user?.perfil || '');
  const podeAlterarTecnico = ['admin_geral', 'admin_empresa', 'supervisor'].includes(user?.perfil || '');

  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [tecnicos, setTecnicos] = useState<Tecnico[]>([]);
  const [tiposChamado, setTiposChamado] = useState<Opcao[]>([]);
  const [servicos, setServicos] = useState<ServicosResposta | null>(null);
  const [carregandoServicos, setCarregandoServicos] = useState(false);
  const [materiaisCatalogo, setMateriaisCatalogo] = useState<MaterialCatalogo[]>([]);
  const [ativos, setAtivos] = useState<Ativo[]>([]);
  const [contexto, setContexto] = useState<ContextoCliente | null>(null);
  const [carregandoContexto, setCarregandoContexto] = useState(false);

  const [clienteId, setClienteId] = useState('');
  const [ativoId, setAtivoId] = useState('');
  const [tipoOs, setTipoOs] = useState('');
  const [tecnicoId, setTecnicoId] = useState(podeAlterarTecnico ? '' : (user?.id ? String(user.id) : ''));
  const [dataAgendada, setDataAgendada] = useState('');
  const [horaAgendada, setHoraAgendada] = useState('');
  const [observacao, setObservacao] = useState('');
  const [itens, setItens] = useState<ItemServico[]>([]);
  const [materiais, setMateriais] = useState<ItemMaterial[]>([]);
  const [novo, setNovo] = useState(NOVO_VAZIO);
  const [novoMaterial, setNovoMaterial] = useState(MATERIAL_VAZIO);
  const [proximaLinha, setProximaLinha] = useState(1);
  const [proximaLinhaMaterial, setProximaLinhaMaterial] = useState(1);

  const [novoCli, setNovoCli] = useState({ nome: '', whatsapp: '' });
  const [mostrarNovoCli, setMostrarNovoCli] = useState(false);
  const [salvandoCli, setSalvandoCli] = useState(false);

  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [criada, setCriada] = useState<{ id: number; codigo?: string } | null>(null);
  const [imprimindo, setImprimindo] = useState(false);
  // Muda a key do <Passos>: sem isso "Criar outra OS" voltaria com o formulário
  // limpo mas parado no último passo, porque o índice do wizard é estado interno
  // dele.
  const [versaoForm, setVersaoForm] = useState(0);

  // Carregamentos INDEPENDENTES de propósito. /cadastros/tecnicos.php e
  // /cadastros/categorias.php exigem perfil gestor/supervisor (restrição que já
  // existia, igual no formulário completo); num Promise.all uma recusa dessas
  // apagaria também a lista de clientes, e a tela abriria vazia sem explicar por
  // quê. Assim cada lista que dá para carregar aparece, e o passo diz o que falta.
  useEffect(() => {
    let ativo = true;
    // Sem Promise.allSettled: o target do build e es5 e o CRA nao poli-enche esse
    // metodo (ES2020). Tres promises soltas fazem o mesmo aqui.
    api<Cliente[]>('/cadastros/clientes.php?somente_ativos=1')
      .then((cli) => { if (ativo) setClientes(cli); })
      // Cliente e o passo 1: sem ele a tela nao serve para nada, entao este erro
      // aparece. Os outros dois falham em silencio e o passo explica o que falta.
      .catch((e) => { if (ativo) setErro(e instanceof Error ? e.message : String(e)); });
    api<Tecnico[]>('/cadastros/tecnicos.php')
      .then((tec) => { if (ativo) setTecnicos(tec); })
      .catch(() => {});
    api<Opcao[]>('/cadastros/categorias.php?somente_ativos=1')
      .then((cat) => {
        if (!ativo) return;
        setTiposChamado(cat);
        setTipoOs((atual) => atual || cat[0]?.nome || '');
      })
      .catch(() => {});
    api<MaterialCatalogo[]>('/estoque/materiais.php')
      .then((mat) => {
        if (!ativo) return;
        setMateriaisCatalogo(mat.filter((m) => (m.status || 'ativo') === 'ativo'));
      })
      .catch(() => {});
    api<Ativo[]>('/ativos/ativos.php')
      .then((lista) => {
        if (!ativo) return;
        setAtivos(lista.filter((a) => (a.status || 'ativo') === 'ativo'));
      })
      .catch(() => {});
    return () => { ativo = false; };
  }, []);

  // Escolhido o cliente, puxa O QUE JÁ ESTÁ CADASTRADO para ele: equipamentos (com
  // ambiente e BTU), ambientes e documentos. É o "não redigite" do fluxo — os dados
  // vivem no cadastro do cliente, a OS só aponta para eles.
  //
  // Falha aqui não trava a criação da OS: a tela cai para a lista global de
  // equipamentos filtrada por vínculo, que é como era antes.
  useEffect(() => {
    if (!clienteId) { setContexto(null); setCarregandoContexto(false); return; }
    let vivo = true;
    setCarregandoContexto(true);
    // Sem .finally(): o target do build é es5 e o CRA não poli-enche esse método.
    api<ContextoCliente>('/os/os_cliente_contexto.php?cliente_id=' + clienteId)
      .then((c) => { if (vivo) { setContexto(c); setCarregandoContexto(false); } })
      .catch(() => { if (vivo) { setContexto(null); setCarregandoContexto(false); } });
    return () => { vivo = false; };
  }, [clienteId]);

  // Sugestões (o que já foi lançado antes) e, se existir, a tabela de preços.
  // Falha aqui não trava nada: o serviço continua podendo ser digitado.
  const carregarServicos = useCallback(async (tipo: string) => {
    setCarregandoServicos(true);
    try {
      const q = tipo ? '?tipo_os=' + encodeURIComponent(tipo) : '';
      setServicos(await api<ServicosResposta>('/os/os_servicos.php' + q));
    } catch {
      setServicos(null);
    } finally {
      setCarregandoServicos(false);
    }
  }, []);

  useEffect(() => { carregarServicos(tipoOs); }, [tipoOs, carregarServicos]);

  const totalServicos = useMemo(
    () => itens.reduce((soma, i) => soma + (i.valor_unitario === null ? 0 : i.valor_unitario * i.quantidade), 0),
    [itens],
  );
  const totalMateriais = useMemo(
    () => materiais.reduce((soma, i) => soma + i.valor_unitario * i.quantidade, 0),
    [materiais],
  );
  const itensSemPreco = useMemo(() => itens.filter((i) => i.valor_unitario === null).length, [itens]);
  const clienteSelecionado = clientes.find((c) => String(c.id) === clienteId);
  const ativosDisponiveis = useMemo(
    () => clienteId
      ? ativos.filter((a) => !(a.cliente_ids || []).length || (a.cliente_ids || []).includes(Number(clienteId)))
      : ativos,
    [ativos, clienteId],
  );
  const ativoSelecionado = ativos.find((a) => String(a.id) === ativoId);
  // Com ficha cadastrada, a lista de equipamentos vem do próprio cliente (traz
  // ambiente, marca/modelo, série e BTU). Sem ficha, mantém o comportamento antigo.
  // Desativado nao aparece: o cadastro nao se exclui, so desativa, e oferecer um
  // aparelho ja retirado numa OS nova mentiria sobre o que esta instalado.
  const equipamentosCliente = (contexto?.equipamentos || []).filter((e) => (e.status || 'ativo') === 'ativo');
  const ambientesCliente = (contexto?.ambientes || []).filter((a) => (a.status || 'ativo') === 'ativo');
  // Contagem por ambiente derivada da lista que a tela mostra. O backend manda a
  // dele, mas se as duas divergirem o usuario ve "0 equipamentos" ao lado de
  // "2 equipamento(s)" - foi exatamente o que aconteceu.
  const equipamentosDoAmbiente = (ambienteId: number): number =>
    equipamentosCliente.filter((e) => e.cliente_ambiente_id === ambienteId).length;
  // Ficha carregada manda na lista, mesmo vazia: cair para a lista global ofereceria
  // equipamento de OUTRO cliente. O fallback fica so para quem nao tem ficha.
  const usarEquipamentosDoCliente = !!contexto;
  const equipamentoCliente = equipamentosCliente.find((e) => String(e.id) === ativoId) || null;
  const ambienteDoEquipamento = equipamentoCliente?.cliente_ambiente_id
    ? ambientesCliente.find((a) => a.id === equipamentoCliente.cliente_ambiente_id) || null
    : null;
  const tecnicoSelecionado = tecnicos.find((t) => String(t.id) === tecnicoId);
  const nomeTecnico = tecnicoSelecionado?.nome
    || (user?.id && String(user.id) === tecnicoId ? (user.nome || 'Usuário logado') : '');
  // Sem passo de localização não há unidade para filtrar o técnico: a lista traz
  // todos, e o backend não aplica a regra de unidade quando a OS não tem unidade.
  const tecnicosResponsavel = !podeAlterarTecnico && user?.id && !tecnicos.some((t) => t.id === user.id)
    ? [{ id: user.id, nome: user.nome || 'Usuário logado' } as Tecnico, ...tecnicos]
    : tecnicos;

  async function cadastrarClienteRapido() {
    if (!novoCli.nome.trim()) { setErro('Informe o nome do cliente.'); return; }
    setSalvandoCli(true);
    setErro('');
    try {
      const r = await api<{ id: number }>('/cadastros/clientes.php', {
        method: 'POST',
        body: { nome: novoCli.nome.trim(), whatsapp: novoCli.whatsapp || null },
      });
      const c: Cliente = { id: r.id, nome: novoCli.nome.trim(), whatsapp: novoCli.whatsapp || null };
      setClientes((atual) => [...atual, c]);
      setClienteId(String(c.id));
      setNovoCli({ nome: '', whatsapp: '' });
      setMostrarNovoCli(false);
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setSalvandoCli(false);
    }
  }

  // Atalho da tabela de servicos: copia descricao/valor e preserva o servico_id.
  function escolherDaTabela(id: string) {
    const o = servicos?.opcoes.find((x) => String(x.id) === id);
    if (!o) { setNovo({ ...novo, servico_id: '' }); return; }
    setNovo({
      servico_id: id,
      material_id: '',
      descricao: o.rotulo,
      valor_unitario: o.valor_unitario === null ? '' : numeroParaMascaraMoeda(o.valor_unitario),
      quantidade: '1',
    });
  }

  // Atalho do estoque: preenche o rascunho de material, que sera gravado como
  // saida de estoque na criacao da OS.
  function escolherDoEstoque(id: string) {
    const m = materiaisCatalogo.find((x) => String(x.id) === id);
    setNovoMaterial({
      material_id: id,
      valor_unitario: m ? numeroParaMascaraMoeda(m.valor_unitario) : '',
      quantidade: novoMaterial.quantidade || '1',
    });
  }

  // Digitar uma descrição que já existe nas sugestões traz o último valor usado.
  function digitarDescricao(texto: string) {
    const s = servicos?.sugestoes.find((x) => x.descricao.toLowerCase() === texto.trim().toLowerCase());
    setNovo((atual) => ({
      ...atual,
      // Editar o texto desliga o vínculo com a linha da tabela de preços: o que
      // vale passa a ser o que está na tela.
      servico_id: '',
      material_id: '',
      descricao: texto,
      valor_unitario: s && !atual.valor_unitario && s.valor_unitario !== null ? numeroParaMascaraMoeda(s.valor_unitario) : atual.valor_unitario,
    }));
  }

  function adicionarServico() {
    setErro('');
    const descricao = novo.descricao.trim();
    if (!descricao) { setErro('Informe a descrição do serviço.'); return; }
    const valor = valorMoedaOuNulo(novo.valor_unitario);
    if (valor !== null && valor < 0) { setErro('O valor do serviço não pode ser negativo.'); return; }

    setItens((atual) => [...atual, {
      linha: proximaLinha,
      servico_id: novo.servico_id ? Number(novo.servico_id) : null,
      descricao,
      valor_unitario: valor,
      quantidade: 1,
    }]);
    setProximaLinha((n) => n + 1);
    setNovo(NOVO_VAZIO);
  }

  function removerServico(linha: number) {
    setItens((atual) => atual.filter((i) => i.linha !== linha));
  }

  function adicionarMaterial() {
    setErro('');
    const materialId = Number(novoMaterial.material_id);
    const mat = materiaisCatalogo.find((m) => m.id === materialId);
    if (!mat) { setErro('Escolha um material do estoque.'); return; }
    const qtd = numeroOuNulo(novoMaterial.quantidade);
    if (qtd === null || qtd <= 0) { setErro('Informe uma quantidade maior que zero para o estoque.'); return; }
    const disponivel = Number(mat.quantidade_atual || 0);
    if (qtd > disponivel) { setErro('Estoque insuficiente para "' + mat.nome + '" (disponivel: ' + qtdTexto(disponivel) + ').'); return; }
    const valor = valorMoedaOuNulo(novoMaterial.valor_unitario) ?? Number(mat.valor_unitario || 0);
    if (valor < 0) { setErro('O valor unitario do estoque nao pode ser negativo.'); return; }
    setMateriais((atual) => [...atual, {
      linha: proximaLinhaMaterial,
      material_id: materialId,
      nome: mat.nome,
      unidade_medida: mat.unidade_medida,
      valor_unitario: valor,
      quantidade: qtd,
    }]);
    setProximaLinhaMaterial((n) => n + 1);
    setNovoMaterial(MATERIAL_VAZIO);
  }

  function removerMaterial(linha: number) {
    setMateriais((atual) => atual.filter((i) => i.linha !== linha));
  }

  function alterarItem(linha: number, campo: 'quantidade' | 'valor_unitario', valor: string) {
    setItens((atual) => atual.map((i) => {
      if (i.linha !== linha) return i;
      const n = campo === 'quantidade' ? numeroOuNulo(valor) : valorMoedaOuNulo(valor);
      if (campo === 'quantidade') return n !== null && n > 0 ? { ...i, quantidade: n } : i;
      // Valor em branco volta a ser "sem preço", que é um estado válido.
      // Editar o valor solta a linha da tabela de preços - senão o backend
      // regravaria o preço do cadastro e a edição sumiria sem aviso.
      return { ...i, valor_unitario: n !== null && n >= 0 ? n : null, servico_id: null };
    }));
  }

  async function criar() {
    setErro('');
    setSalvando(true);
    try {
      const body = {
        cliente_id: Number(clienteId),
        ...(ativoId ? { ativo_id: Number(ativoId) } : {}),
        tipo_os: tipoOs,
        prioridade: 'media',
        ...(tecnicoId ? { tecnico_id: Number(tecnicoId) } : {}),
        data_agendada: dataAgendada || null,
        hora_agendada: horaAgendada || null,
        observacao: observacao.trim() || null,
        servicos: itens.map((i) => (
          i.servico_id !== null
            ? { servico_id: i.servico_id, quantidade: i.quantidade }
            : { descricao: i.descricao, valor_unitario: i.valor_unitario, quantidade: i.quantidade }
        )),
        materiais: materiais.map((m) => ({
          material_id: m.material_id,
          quantidade: m.quantidade,
          valor_unitario: m.valor_unitario,
        })),
      };
      const data = await api<{ id: number; codigo?: string }>('/os/os_criar.php', { method: 'POST', body });
      setCriada({ id: data.id, codigo: data.codigo });
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setSalvando(false);
    }
  }

  // Documento imprimível da OS. O link direto não carrega o header Authorization,
  // então buscamos com o token e abrimos o resultado numa aba - mesmo caminho do
  // botão de documento no detalhe da OS.
  async function imprimir(osId: number) {
    setErro('');
    setImprimindo(true);
    const win = window.open('', '_blank'); // aberto no gesto do clique, senão o pop-up é bloqueado
    if (win) win.document.write('Gerando a OS para impressão...');
    try {
      const res = await fetch(`${API_BASE}/os/os_pdf.php?id=${osId}`, { headers: { Authorization: 'Bearer ' + getToken() } });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      if (win) {
        win.location.href = url;
        // Chama a impressão sozinho quando o navegador permite; quando não
        // permite, o documento já está aberto e o usuário imprime pelo menu.
        try {
          win.addEventListener('load', () => { try { win.print(); } catch { /* usuário imprime pelo menu */ } });
        } catch { /* navegador não deixa observar a aba: segue aberta */ }
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      if (win) win.close();
      setErro('Não foi possível abrir a OS para impressão. Ela foi criada e pode ser impressa pelo detalhe da OS.');
    } finally {
      setImprimindo(false);
    }
  }

  // "Criar outra OS": zera o formulário em vez de recarregar a página - clientes,
  // técnicos e sugestões já estão carregados.
  function reiniciar() {
    setCriada(null);
    setErro('');
    setClienteId('');
    setAtivoId('');
    setTecnicoId(podeAlterarTecnico ? '' : (user?.id ? String(user.id) : ''));
    setDataAgendada('');
    setHoraAgendada('');
    setObservacao('');
    setItens([]);
    setMateriais([]);
    setNovo(NOVO_VAZIO);
    setNovoMaterial(MATERIAL_VAZIO);
    setMostrarNovoCli(false);
    setNovoCli({ nome: '', whatsapp: '' });
    setVersaoForm((v) => v + 1);
    carregarServicos(tipoOs); // a OS recém-criada virou sugestão
  }

  const tituloSecao = {
    fontSize: 12,
    fontWeight: 800,
    textTransform: 'uppercase' as const,
    letterSpacing: '.08em',
    color: 'var(--texto-suave)',
    margin: '18px 2px 8px',
  };

  // Caixa "o que já está cadastrado para este cliente" (passo 1 e revisão).
  const caixaContexto = {
    marginTop: 14,
    border: '1px solid var(--cinza-borda)',
    borderRadius: 12,
    padding: 12,
  };
  const tituloContexto = { ...tituloSecao, margin: '0 2px 6px' };
  const listaContexto = {
    margin: '0 0 10px',
    padding: '0 0 0 18px',
    fontSize: 12.5,
    lineHeight: 1.6,
    color: 'var(--texto-suave)',
  };

  const colunasTabela = (editavel: boolean) => (podeVerValoresMonetarios ? 3 : 1) + (editavel ? 1 : 0);

  const tabelaServicos = (editavel: boolean) => (
    <div className="tabela-wrap" style={{ marginTop: 12 }}>
      <table>
        <thead>
          <tr>
            <th>Serviço</th>
            {podeVerValoresMonetarios && <th style={{ width: 150 }}>Valor unit.</th>}
            {podeVerValoresMonetarios && <th style={{ width: 130 }}>Total</th>}
            {editavel && <th style={{ width: 90 }} />}
          </tr>
        </thead>
        <tbody>
          {itens.map((i) => (
            <tr key={i.linha}>
              <td>{i.descricao}</td>
              {podeVerValoresMonetarios && (
                <td>
                  {editavel ? (
                    <input
                      className="input"
                      inputMode="decimal"
                      value={i.valor_unitario === null ? '' : numeroParaMascaraMoeda(i.valor_unitario)}
                      placeholder="sem preço"
                      onChange={(e) => alterarItem(i.linha, 'valor_unitario', mascaraMoedaBR(e.target.value))}
                      style={{ width: 120 }}
                    />
                  ) : (
                    i.valor_unitario === null
                      ? <span style={{ color: 'var(--texto-suave)', fontStyle: 'italic' }}>sem preço</span>
                      : moeda(i.valor_unitario)
                  )}
                </td>
              )}
              {podeVerValoresMonetarios && (
                <td>{i.valor_unitario === null ? '—' : moeda(i.valor_unitario * i.quantidade)}</td>
              )}
              {editavel && (
                <td style={{ textAlign: 'right' }}>
                  <button type="button" className="btn danger sm" onClick={() => removerServico(i.linha)}>Remover</button>
                </td>
              )}
            </tr>
          ))}
          {!itens.length && (
            <tr><td colSpan={colunasTabela(editavel)} className="vazio">Nenhum serviço lançado.</td></tr>
          )}
          {podeVerValoresMonetarios && (
            <tr>
              <th colSpan={editavel ? 2 : 1} style={{ textAlign: 'right' }}>Total da OS</th>
              <th>{moeda(totalServicos)}</th>
              {editavel && <th />}
            </tr>
          )}
        </tbody>
      </table>
      {podeVerValoresMonetarios && itensSemPreco > 0 && (
        <p className="muted" style={{ margin: '6px 0 0', fontSize: 12 }}>
          {itensSemPreco === 1
            ? '1 serviço está sem preço e não entra no total.'
            : itensSemPreco + ' serviços estão sem preço e não entram no total.'}
        </p>
      )}
    </div>
  );

  const tabelaMateriais = (editavel: boolean) => (
    <div className="tabela-wrap" style={{ marginTop: 12 }}>
      <table>
        <thead>
          <tr>
            <th>Estoque</th>
            {podeVerValoresMonetarios && <th style={{ width: 150 }}>Valor unit.</th>}
            {podeVerValoresMonetarios && <th style={{ width: 130 }}>Total</th>}
            {editavel && <th style={{ width: 90 }} />}
          </tr>
        </thead>
        <tbody>
          {materiais.map((m) => (
            <tr key={m.linha}>
              <td>{m.nome}{m.unidade_medida ? ' (' + m.unidade_medida + ')' : ''}</td>
              <td>{qtdTexto(m.quantidade)}</td>
              {podeVerValoresMonetarios && <td>{moeda(m.valor_unitario)}</td>}
              {podeVerValoresMonetarios && <td>{moeda(m.valor_unitario * m.quantidade)}</td>}
              {editavel && (
                <td style={{ textAlign: 'right' }}>
                  <button type="button" className="btn danger sm" onClick={() => removerMaterial(m.linha)}>Remover</button>
                </td>
              )}
            </tr>
          ))}
          {!materiais.length && (
            <tr><td colSpan={(podeVerValoresMonetarios ? 4 : 2) + (editavel ? 1 : 0)} className="vazio">Nenhum material do estoque lançado.</td></tr>
          )}
          {podeVerValoresMonetarios && (
            <tr>
              <th colSpan={editavel ? 3 : 2} style={{ textAlign: 'right' }}>Total de estoque</th>
              <th>{moeda(totalMateriais)}</th>
              {editavel && <th />}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  // ------------------------------------------------------------------ Imprimir
  // Último passo do fluxo. A OS já existe aqui: em vez de um passo do wizard que
  // daria para navegar de volta e recriar a OS, a tela troca de estado.
  if (criada) {
    return (
      <div>
        <div className="titulo-pg"><h1>OS criada</h1></div>
        {erro && <div className="msg-erro">{erro}</div>}
        <div className="card">
          <h3 style={{ marginTop: 0 }}>
            OS {criada.codigo || '#' + criada.id} criada para {clienteSelecionado?.nome || 'o cliente'}.
          </h3>
          {podeVerValoresMonetarios && (
            <p style={{ margin: '4px 0 0' }}>
              {itens.length
                ? itens.length + ' serviço(s) lançado(s) — total ' + moeda(totalServicos) + '.'
                : 'Nenhum serviço lançado.'}
            </p>
          )}
          <div className="form-actions" style={{ marginTop: 18 }}>
            <button type="button" className="btn sec" onClick={() => nav('/app/os/' + criada.id)}>Abrir a OS</button>
            <button type="button" className="btn" disabled={imprimindo} onClick={() => imprimir(criada.id)}>
              {imprimindo ? 'Abrindo...' : 'Imprimir OS'}
            </button>
          </div>
          <p className="muted" style={{ margin: '12px 0 0', fontSize: 12 }}>
            A OS abre numa aba nova ja pronta para imprimir. Servicos e materiais informados aqui
            ja ficam vinculados a OS.
          </p>
          <div style={{ marginTop: 14 }}>
            <button type="button" className="btn sec sm" onClick={reiniciar}>+ Criar outra OS</button>
          </div>
        </div>
      </div>
    );
  }

  const passos: Passo[] = [
    {
      titulo: 'Cliente',
      validar: () => (clienteId ? null : 'Escolha o cliente da OS.'),
      node: (
        <div className="card">
          {!mostrarNovoCli ? (
            <>
              <label>Cliente *</label>
              <select value={clienteId} onChange={(e) => { setClienteId(e.target.value); setAtivoId(''); }}>
                <option value="">Selecione o cliente</option>
                {clientes.map((c) => <option key={c.id} value={c.id}>{c.nome}{c.whatsapp ? ' · ' + c.whatsapp : ''}</option>)}
              </select>
              <button type="button" className="btn sec sm" style={{ marginTop: 8 }} onClick={() => setMostrarNovoCli(true)}>+ Novo cliente</button>
              {!clientes.length && (
                <p className="muted" style={{ margin: '8px 0 0', fontSize: 12 }}>
                  Nenhum cliente cadastrado ainda. Cadastre aqui mesmo com o botão acima.
                </p>
              )}

              {/* O que já está cadastrado para este cliente. Aparece aqui, no passo 1,
                  para o técnico ver com o que vai lidar antes de montar a OS. */}
              {!!clienteId && carregandoContexto && (
                <p className="muted" style={{ margin: '12px 0 0', fontSize: 12 }}>Carregando o cadastro do cliente...</p>
              )}
              {!!clienteId && !carregandoContexto && !contexto && (
                <p className="muted" style={{ margin: '12px 0 0', fontSize: 12 }}>
                  Este cliente ainda não tem equipamentos, ambientes ou documentos cadastrados.
                  Cadastre em Cadastros &gt; Clientes.
                </p>
              )}
              {!!clienteId && !carregandoContexto && contexto && (
                <div style={caixaContexto}>
                  <div style={tituloContexto}>Cadastro do cliente</div>
                  {(contexto.cliente.documento || contexto.cliente.endereco_linha) && (
                    <p className="muted" style={{ margin: '0 0 10px', fontSize: 12 }}>
                      {[contexto.cliente.documento, contexto.cliente.endereco_linha].filter(Boolean).join(' · ')}
                    </p>
                  )}

                  <div style={tituloContexto}>Equipamentos ({equipamentosCliente.length})</div>
                  {equipamentosCliente.length ? (
                    <ul style={listaContexto}>
                      {equipamentosCliente.map((e) => (
                        <li key={e.id}>{rotuloEquipamentoCliente(e)}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="muted" style={{ margin: '0 0 10px', fontSize: 12 }}>Nenhum equipamento ativo cadastrado.</p>
                  )}

                  <div style={tituloContexto}>Ambientes ({ambientesCliente.length})</div>
                  {ambientesCliente.length ? (
                    <ul style={listaContexto}>
                      {ambientesCliente.map((a) => (
                        <li key={a.id}>
                          {[
                            a.nome,
                            a.atividade_ambiente,
                            areaTexto(a.area_m2),
                            a.ocupantes_max ? a.ocupantes_max + ' ocupante(s)' : '',
                            equipamentosDoAmbiente(a.id) + ' equipamento(s)',
                          ].filter(Boolean).join(' · ')}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="muted" style={{ margin: '0 0 10px', fontSize: 12 }}>Nenhum ambiente ativo cadastrado.</p>
                  )}

                  <div style={tituloContexto}>Documentos ({contexto.documentos.length})</div>
                  {contexto.documentos.length ? (
                    <ul style={listaContexto}>
                      {contexto.documentos.map((d) => (
                        <li key={d.id}>
                          {[
                            d.titulo,
                            d.data_validade ? 'validade ' + dataTexto(d.data_validade) : '',
                          ].filter(Boolean).join(' · ')}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="muted" style={{ margin: '0 0 10px', fontSize: 12 }}>Nenhum documento cadastrado.</p>
                  )}

                  <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
                    Tudo isso sai impresso no documento da OS.
                  </p>
                </div>
              )}
            </>
          ) : (
            <div style={{ border: '1px dashed var(--borda)', borderRadius: 10, padding: 12 }}>
              <label>Nome do cliente *</label>
              <input className="input" value={novoCli.nome} onChange={(e) => setNovoCli({ ...novoCli, nome: e.target.value })} />
              <label>WhatsApp</label>
              <input className="input" value={novoCli.whatsapp} onChange={(e) => setNovoCli({ ...novoCli, whatsapp: e.target.value })} placeholder="(11) 99999-9999" />
              <div className="form-actions">
                <button type="button" className="btn sec" onClick={() => { setMostrarNovoCli(false); setNovoCli({ nome: '', whatsapp: '' }); }}>Cancelar</button>
                <button type="button" className="btn" disabled={salvandoCli} onClick={cadastrarClienteRapido}>{salvandoCli ? 'Salvando...' : 'Salvar cliente'}</button>
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      titulo: 'Serviços',
      validar: () => (tipoOs ? null : 'Escolha o tipo de chamado.'),
      node: (
        <div className="card">
          <label>Tipo de chamado *</label>
          <select value={tipoOs} onChange={(e) => setTipoOs(e.target.value)} disabled={!tiposChamado.length}>
            <option value="">Selecione o tipo de chamado</option>
            {tiposChamado.map((c) => <option key={c.id} value={c.nome}>{c.nome}</option>)}
          </select>
          {!tiposChamado.length && (
            <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
              Nenhum tipo de chamado cadastrado ainda. Cadastre um em Categorias antes de abrir uma OS.
            </p>
          )}
          {/* Cliente com ficha cadastrada: a lista e a DELE, com ambiente e BTU.
              Sem ficha, cai na lista global filtrada por vinculo (comportamento antigo). */}
          {usarEquipamentosDoCliente ? (
            <>
              <label style={{ marginTop: 12 }}>Equipamento do cliente (opcional)</label>
              <select value={ativoId} onChange={(e) => setAtivoId(e.target.value)} disabled={!equipamentosCliente.length}>
                <option value="">OS sem equipamento vinculado</option>
                {equipamentosCliente.map((e) => (
                  <option key={e.id} value={e.id}>{rotuloEquipamentoCliente(e)}</option>
                ))}
              </select>
              {!equipamentosCliente.length && (
                <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
                  Este cliente não tem equipamento ativo cadastrado. Cadastre em Cadastros &gt; Clientes,
                  no passo Equipamentos, para ele aparecer aqui.
                </p>
              )}
              {equipamentoCliente && (
                <div style={caixaContexto}>
                  <div style={tituloContexto}>Equipamento escolhido</div>
                  <ul style={listaContexto}>
                    {equipamentoCliente.categoria_nome && <li>Tipo: {equipamentoCliente.categoria_nome}</li>}
                    {(equipamentoCliente.marca || equipamentoCliente.modelo) && (
                      <li>Marca / modelo: {[equipamentoCliente.marca, equipamentoCliente.modelo].filter(Boolean).join(' / ')}</li>
                    )}
                    {equipamentoCliente.numero_serie && <li>{user?.rotulo_numero_serie || 'Número de série'}: {equipamentoCliente.numero_serie}</li>}
                    {equipamentoCliente.patrimonio && <li>Patrimônio: {equipamentoCliente.patrimonio}</li>}
                    {!!equipamentoCliente.capacidade_btu && <li>Capacidade: {btuTexto(equipamentoCliente.capacidade_btu)}</li>}
                    {equipamentoCliente.ambiente_nome && <li>Ambiente: {equipamentoCliente.ambiente_nome}</li>}
                    {ambienteDoEquipamento && (
                      <li>
                        {[
                          ambienteDoEquipamento.atividade_ambiente,
                          areaTexto(ambienteDoEquipamento.area_m2),
                          ambienteDoEquipamento.ocupantes_max ? ambienteDoEquipamento.ocupantes_max + ' ocupante(s)' : '',
                        ].filter(Boolean).join(' · ') || 'Ambiente sem medidas cadastradas'}
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </>
          ) : !!ativosDisponiveis.length && (
            <>
              <label style={{ marginTop: 12 }}>Equipamento (opcional)</label>
              <select value={ativoId} onChange={(e) => setAtivoId(e.target.value)}>
                <option value="">OS sem equipamento vinculado</option>
                {ativosDisponiveis.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.nome}{a.patrimonio ? ' - ' + a.patrimonio : ''}
                  </option>
                ))}
              </select>
            </>
          )}

          <div style={tituloSecao}>Serviço</div>

          {!!servicos?.opcoes.length && (
            <>
              <label>Serviço da tabela (opcional)</label>
              <select value={novo.servico_id} onChange={(e) => escolherDaTabela(e.target.value)}>
                <option value="">Digitar o serviço abaixo</option>
                {servicos.opcoes.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.rotulo}
                    {podeVerValoresMonetarios
                      ? (o.valor_unitario === null ? ' - sem preço' : ' - ' + moeda(Number(o.valor_unitario)))
                      : ''}
                  </option>
                ))}
              </select>
            </>
          )}

          <div className="grid cols2" style={{ gap: 8, alignItems: 'end', marginTop: !!servicos?.opcoes.length ? 12 : 0 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Serviço *</label>
              <input
                className="input"
                list="sugestoes-servico-os"
                value={novo.descricao}
                onChange={(e) => digitarDescricao(e.target.value)}
                placeholder="Ex.: Limpeza completa de split 12.000 BTU"
              />
              <datalist id="sugestoes-servico-os">
                {(servicos?.sugestoes || []).map((s) => <option key={s.descricao} value={s.descricao} />)}
              </datalist>
            </div>
            {podeVerValoresMonetarios && (
              <div>
                <label>Valor unitário</label>
                <input
                  className="input"
                  inputMode="decimal"
                  value={novo.valor_unitario}
                  onChange={(e) => setNovo({ ...novo, material_id: '', servico_id: '', valor_unitario: mascaraMoedaBR(e.target.value) })}
                  placeholder="deixe vazio para sem preço"
                />
              </div>
            )}
            <div className="form-actions" style={{ margin: 0, gridColumn: '1 / -1' }}>
              <button type="button" className="btn" onClick={adicionarServico}>Adicionar serviço</button>
            </div>
          </div>

          <p className="muted" style={{ margin: '6px 0 0', fontSize: 12 }}>
            {carregandoServicos
              ? 'Carregando sugestões...'
              : servicos?.sugestoes.length
                ? 'O campo sugere os ' + servicos.sugestoes.length + ' serviço(s) já lançados antes, com o último valor usado.'
                : 'Digite o serviço e o valor. A partir da segunda OS, o campo passa a sugerir o que você já lançou.'}
          </p>

          {tabelaServicos(true)}

          <div style={tituloSecao}>Estoque</div>
          {!!materiaisCatalogo.length ? (
            <>
              <label>Material do estoque</label>
              <select value={novoMaterial.material_id} onChange={(e) => escolherDoEstoque(e.target.value)}>
                <option value="">Selecione o material</option>
                {materiaisCatalogo.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nome}
                    {m.unidade_medida ? ' - ' + m.unidade_medida : ''}
                    {' - estoque ' + Number(m.quantidade_atual || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3, useGrouping: false })}
                    {podeVerValoresMonetarios ? ' - ' + moeda(Number(m.valor_unitario || 0)) : ''}
                  </option>
                ))}
              </select>
              <div className="grid cols2" style={{ gap: 8, alignItems: 'end', marginTop: 12 }}>
                {podeVerValoresMonetarios && (
                  <div>
                    <label>Valor unitário</label>
                    <input
                      className="input"
                      inputMode="decimal"
                      value={novoMaterial.valor_unitario}
                      onChange={(e) => setNovoMaterial({ ...novoMaterial, valor_unitario: mascaraMoedaBR(e.target.value) })}
                      placeholder="usa o valor cadastrado se vazio"
                    />
                  </div>
                )}
                <div>
                  <label>Quantidade</label>
                  <input
                    className="input"
                    type="number"
                    min="0.001"
                    step="0.001"
                    value={novoMaterial.quantidade}
                    onChange={(e) => setNovoMaterial({ ...novoMaterial, quantidade: e.target.value })}
                  />
                </div>
                <div className="form-actions" style={{ margin: 0, gridColumn: '1 / -1' }}>
                  <button type="button" className="btn sec" onClick={adicionarMaterial}>Adicionar estoque</button>
                </div>
              </div>
            </>
          ) : (
            <p className="muted" style={{ margin: 0, fontSize: 12 }}>Nenhum material ativo no estoque.</p>
          )}
          {tabelaMateriais(true)}
        </div>
      ),
    },
    {
      titulo: 'Técnico responsável',
      node: (
        <div className="card">
          <label>Técnico</label>
          <select value={tecnicoId} onChange={(e) => setTecnicoId(e.target.value)} disabled={!podeAlterarTecnico}>
            <option value="">Responsável abre a OS</option>
            {tecnicosResponsavel.map((t) => <option key={t.id} value={t.id}>{t.id === user?.id ? t.nome + ' (você)' : t.nome}</option>)}
          </select>
          {!podeAlterarTecnico && (
            <p className="muted" style={{ margin: '6px 0 0', fontSize: 13 }}>A OS será atribuída ao usuário logado.</p>
          )}

          <div style={tituloSecao}>Agendamento (opcional)</div>
          <div className="grid cols2">
            <div>
              <label>Data</label>
              <input className="input" type="date" value={dataAgendada} onChange={(e) => setDataAgendada(e.target.value)} />
            </div>
            <div>
              <label>Hora</label>
              <input className="input" type="time" value={horaAgendada} onChange={(e) => setHoraAgendada(e.target.value)} />
            </div>
          </div>

          <div style={tituloSecao}>Observação (opcional)</div>
          <textarea rows={3} value={observacao} onChange={(e) => setObservacao(e.target.value)} placeholder="O que o técnico precisa saber antes de ir" />
        </div>
      ),
    },
    {
      titulo: 'Revisão',
      node: (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Revisão</h3>
          <div className="os-dados">
            <div className="os-campo"><span className="k">Cliente</span><span className="v">{clienteSelecionado?.nome || '—'}</span></div>
            <div className="os-campo"><span className="k">Tipo de chamado</span><span className="v">{tipoOs || '—'}</span></div>
            <div className="os-campo">
              <span className="k">{user?.rotulo_nome_ativo || 'Equipamento'}</span>
              <span className="v">{equipamentoCliente ? rotuloEquipamentoCliente(equipamentoCliente) : (ativoSelecionado?.nome || '—')}</span>
            </div>
            {(equipamentoCliente?.ambiente_nome || ambienteDoEquipamento) && (
              <div className="os-campo">
                <span className="k">Ambiente</span>
                <span className="v">
                  {[
                    equipamentoCliente?.ambiente_nome,
                    ambienteDoEquipamento?.atividade_ambiente,
                    areaTexto(ambienteDoEquipamento?.area_m2 ?? null),
                  ].filter(Boolean).join(' · ')}
                </span>
              </div>
            )}
            <div className="os-campo"><span className="k">Técnico</span><span className="v">{nomeTecnico || 'Responsável abre a OS'}</span></div>
            <div className="os-campo">
              <span className="k">Agendamento</span>
              <span className="v">
                {dataAgendada
                  ? dataAgendada.split('-').reverse().join('/') + (horaAgendada ? ' ' + horaAgendada : '')
                  : 'Sem agendamento'}
              </span>
            </div>
            {observacao.trim() && (
              <div className="os-campo full"><span className="k">Observação</span><span className="v bloco">{observacao}</span></div>
            )}
          </div>

          {tabelaServicos(false)}
          {tabelaMateriais(false)}

          <div className="form-actions" style={{ marginTop: 18 }}>
            <button type="button" className="btn sec" onClick={() => nav('/app/os')}>Cancelar</button>
            <button type="button" className="btn" disabled={salvando || !clienteId || !tipoOs} onClick={criar}>
              {salvando ? 'Criando...' : 'Criar OS'}
            </button>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="titulo-pg"><h1>Nova OS</h1></div>
      <p className="muted" style={{ margin: '0 0 14px', fontSize: 13 }}>
        Cliente → Serviços → Técnico responsável → Revisão → Imprimir.{' '}
        <Link to="/app/os/nova?completa=1">Usar o formulário completo</Link> para informar localização,
        equipamento, prioridade, orçamento e evidências.
      </p>
      {erro && <div className="msg-erro">{erro}</div>}
      <Passos key={versaoForm} passos={passos} rotuloFim="Revise e crie a OS" />
    </div>
  );
}
